import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Tabs,
  Input,
  Select,
  Avatar,
  Badge,
  Tag,
  Button,
  Space,
  Typography,
  Tooltip,
  Progress,
  Divider,
  Empty,
  Spin,
  theme,
  Radio,
  Rate,
  Flex,
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  ShopOutlined,
  SearchOutlined,
  FilterOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  StarFilled,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  FireOutlined,
  CrownOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { getChartSeriesColors, hexToRgba } from '../../shared/styles';

const { Text, Title } = Typography;
const { Search } = Input;

const AssignmentWidget = ({ 
  orderId = 'REQ-2847',
  vehicle = 'BMW M4 Competition',
  product = 'Stage 2',
  onAssign,
  initialAssignment = null,
}) => {
  const [activeTab, setActiveTab] = useState('tuner');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(initialAssignment?.id || null);
  const [selectedType, setSelectedType] = useState(initialAssignment?.type || null);
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Filters
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [specialityFilter, setSpecialityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('workload');
  const { token } = theme.useToken();
  const chartColors = useMemo(() => getChartSeriesColors(token), [token]);

  // Mock data - Tuners (individuals)
  const tuners = useMemo(() => {
    const p = (i: number) => chartColors[i % chartColors.length];
    return [
    { 
      id: 1, 
      name: 'Thomas Martin', 
      avatar: 'TM', 
      color: p(0),
      role: 'Senior Tuner',
      available: true,
      workload: 8,
      maxWorkload: 15,
      completedToday: 5,
      avgTime: 38,
      rating: 4.9,
      specialities: ['BMW', 'Audi', 'VAG'],
      currentTask: null,
    },
    { 
      id: 2, 
      name: 'Julie Rousseau', 
      avatar: 'JR', 
      color: p(1),
      role: 'Tuner',
      available: true,
      workload: 12,
      maxWorkload: 15,
      completedToday: 7,
      avgTime: 42,
      rating: 4.8,
      specialities: ['Mercedes', 'BMW', 'Porsche'],
      currentTask: 'Audi RS6 - Stage 1',
    },
    { 
      id: 3, 
      name: 'Marc Dubois', 
      avatar: 'MD', 
      color: p(2),
      role: 'Tuner',
      available: false,
      workload: 15,
      maxWorkload: 15,
      completedToday: 8,
      avgTime: 45,
      rating: 4.7,
      specialities: ['VAG', 'Diesel'],
      currentTask: 'VW Golf R - Full Delete',
    },
    { 
      id: 4, 
      name: 'Sophie Laurent', 
      avatar: 'SL', 
      color: p(3),
      role: 'Junior Tuner',
      available: true,
      workload: 5,
      maxWorkload: 10,
      completedToday: 3,
      avgTime: 55,
      rating: 4.5,
      specialities: ['Renault', 'Peugeot', 'Citroën'],
      currentTask: null,
    },
    { 
      id: 5, 
      name: 'Pierre Bernard', 
      avatar: 'PB', 
      color: p(4),
      role: 'Senior Tuner',
      available: true,
      workload: 10,
      maxWorkload: 15,
      completedToday: 6,
      avgTime: 35,
      rating: 4.9,
      specialities: ['BMW', 'Mercedes', 'Performance'],
      currentTask: 'Mercedes C63 - Stage 2',
    },
    { 
      id: 6, 
      name: 'Emma Garcia', 
      avatar: 'EG', 
      color: p(5),
      role: 'Tuner',
      available: true,
      workload: 7,
      maxWorkload: 12,
      completedToday: 4,
      avgTime: 48,
      rating: 4.6,
      specialities: ['Diesel', 'Trucks', 'Agriculture'],
      currentTask: null,
    },
  ];
  }, [chartColors]);

  // Mock data - Groups
  const groups = useMemo(() => {
    const p = (i: number) => chartColors[i % chartColors.length];
    return [
    {
      id: 101,
      name: 'Équipe Performance',
      icon: '🏎️',
      color: p(0),
      members: ['Thomas M.', 'Pierre B.', 'Julie R.'],
      memberCount: 3,
      totalWorkload: 30,
      maxWorkload: 45,
      avgResponseTime: 35,
      specialities: ['BMW', 'Mercedes', 'Audi', 'Porsche'],
      activeRequests: 12,
      description: 'Spécialisée dans les véhicules haute performance',
    },
    {
      id: 102,
      name: 'Équipe Diesel',
      icon: '🛢️',
      color: p(1),
      members: ['Marc D.', 'Emma G.'],
      memberCount: 2,
      totalWorkload: 22,
      maxWorkload: 27,
      avgResponseTime: 45,
      specialities: ['Diesel', 'FAP', 'EGR', 'AdBlue'],
      activeRequests: 8,
      description: 'Experts en motorisation diesel et suppressions',
    },
    {
      id: 103,
      name: 'Équipe VAG',
      icon: '🔧',
      color: p(2),
      members: ['Thomas M.', 'Marc D.', 'Sophie L.'],
      memberCount: 3,
      totalWorkload: 28,
      maxWorkload: 40,
      avgResponseTime: 40,
      specialities: ['VW', 'Audi', 'Skoda', 'Seat', 'DSG'],
      activeRequests: 10,
      description: 'Spécialistes du groupe Volkswagen',
    },
    {
      id: 104,
      name: 'Équipe Française',
      icon: '🇫🇷',
      color: p(3),
      members: ['Sophie L.'],
      memberCount: 1,
      totalWorkload: 5,
      maxWorkload: 10,
      avgResponseTime: 55,
      specialities: ['Renault', 'Peugeot', 'Citroën', 'DS'],
      activeRequests: 3,
      description: 'Experts véhicules français',
    },
  ];
  }, [chartColors]);

  // Mock data - Service Providers (companies)
  const providers = useMemo(() => {
    const p = (i: number) => chartColors[i % chartColors.length];
    return [
    {
      id: 201,
      name: 'JI Engineering',
      logo: 'JI',
      color: p(0),
      type: 'Premium Partner',
      location: 'Munich, Allemagne',
      rating: 4.9,
      totalJobs: 1234,
      responseTime: '< 30min',
      specialities: ['BMW', 'Audi', 'VAG', 'Mercedes'],
      priceLevel: 'premium',
      activeCapacity: 85,
      verified: true,
      fastResponse: true,
    },
    {
      id: 202,
      name: 'TuneMax Pro',
      logo: 'TM',
      color: p(1),
      type: 'Partner',
      location: 'Stuttgart, Allemagne',
      rating: 4.8,
      totalJobs: 856,
      responseTime: '< 1h',
      specialities: ['Mercedes', 'Porsche', 'AMG'],
      priceLevel: 'premium',
      activeCapacity: 70,
      verified: true,
      fastResponse: true,
    },
    {
      id: 203,
      name: 'EcuTech Solutions',
      logo: 'ET',
      color: p(2),
      type: 'Partner',
      location: 'Lyon, France',
      rating: 4.7,
      totalJobs: 2145,
      responseTime: '< 1h',
      specialities: ['Renault', 'Peugeot', 'Citroën', 'Diesel'],
      priceLevel: 'standard',
      activeCapacity: 60,
      verified: true,
      fastResponse: false,
    },
    {
      id: 204,
      name: 'German Power Tuning',
      logo: 'GP',
      color: p(3),
      type: 'Premium Partner',
      location: 'Francfort, Allemagne',
      rating: 4.9,
      totalJobs: 3421,
      responseTime: '< 30min',
      specialities: ['BMW', 'Mercedes', 'Audi', 'Porsche'],
      priceLevel: 'premium',
      activeCapacity: 90,
      verified: true,
      fastResponse: true,
    },
    {
      id: 205,
      name: 'Italian Motors Expert',
      logo: 'IM',
      color: p(4),
      type: 'Specialist',
      location: 'Milan, Italie',
      rating: 5.0,
      totalJobs: 432,
      responseTime: '< 2h',
      specialities: ['Ferrari', 'Lamborghini', 'Maserati', 'Alfa Romeo'],
      priceLevel: 'luxury',
      activeCapacity: 45,
      verified: true,
      fastResponse: false,
    },
    {
      id: 206,
      name: 'Diesel Performance Pro',
      logo: 'DP',
      color: p(5),
      type: 'Partner',
      location: 'Bruxelles, Belgique',
      rating: 4.6,
      totalJobs: 1876,
      responseTime: '< 1h',
      specialities: ['Diesel', 'Trucks', 'Agriculture', 'Marine'],
      priceLevel: 'standard',
      activeCapacity: 55,
      verified: true,
      fastResponse: true,
    },
  ];
  }, [chartColors]);

  // Get all unique specialities
  const allSpecialities = useMemo(() => {
    const specs = new Set();
    tuners.forEach(t => t.specialities.forEach(s => specs.add(s)));
    groups.forEach(g => g.specialities.forEach(s => specs.add(s)));
    providers.forEach(p => p.specialities.forEach(s => specs.add(s)));
    return ['all', ...Array.from(specs).sort()];
  }, []);

  // Filter and sort tuners
  const filteredTuners = useMemo(() => {
    let result = [...tuners];

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.specialities.some(s => s.toLowerCase().includes(query))
      );
    }

    // Availability filter
    if (availabilityFilter === 'available') {
      result = result.filter(t => t.available && t.workload < t.maxWorkload);
    } else if (availabilityFilter === 'busy') {
      result = result.filter(t => !t.available || t.workload >= t.maxWorkload);
    }

    // Speciality filter
    if (specialityFilter !== 'all') {
      result = result.filter(t => t.specialities.includes(specialityFilter));
    }

    // Sort
    if (sortBy === 'workload') {
      result.sort((a, b) => (a.workload / a.maxWorkload) - (b.workload / b.maxWorkload));
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'speed') {
      result.sort((a, b) => a.avgTime - b.avgTime);
    }

    return result;
  }, [tuners, searchQuery, availabilityFilter, specialityFilter, sortBy]);

  // Filter and sort groups
  const filteredGroups = useMemo(() => {
    let result = [...groups];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(g => 
        g.name.toLowerCase().includes(query) ||
        g.specialities.some(s => s.toLowerCase().includes(query))
      );
    }

    if (specialityFilter !== 'all') {
      result = result.filter(g => g.specialities.includes(specialityFilter));
    }

    if (sortBy === 'workload') {
      result.sort((a, b) => (a.totalWorkload / a.maxWorkload) - (b.totalWorkload / b.maxWorkload));
    } else if (sortBy === 'speed') {
      result.sort((a, b) => a.avgResponseTime - b.avgResponseTime);
    }

    return result;
  }, [groups, searchQuery, specialityFilter, sortBy]);

  // Filter and sort providers
  const filteredProviders = useMemo(() => {
    let result = [...providers];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.specialities.some(s => s.toLowerCase().includes(query)) ||
        p.location.toLowerCase().includes(query)
      );
    }

    if (availabilityFilter === 'available') {
      result = result.filter(p => p.activeCapacity < 80);
    } else if (availabilityFilter === 'busy') {
      result = result.filter(p => p.activeCapacity >= 80);
    }

    if (specialityFilter !== 'all') {
      result = result.filter(p => p.specialities.includes(specialityFilter));
    }

    if (sortBy === 'workload') {
      result.sort((a, b) => a.activeCapacity - b.activeCapacity);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'speed') {
      result.sort((a, b) => {
        const timeA = parseInt(a.responseTime.match(/\d+/)[0]);
        const timeB = parseInt(b.responseTime.match(/\d+/)[0]);
        return timeA - timeB;
      });
    }

    return result;
  }, [providers, searchQuery, availabilityFilter, specialityFilter, sortBy]);

  // Handle selection
  const handleSelect = (id, type) => {
    if (selectedId === id && selectedType === type) {
      setSelectedId(null);
      setSelectedType(null);
    } else {
      setSelectedId(id);
      setSelectedType(type);
    }
  };

  // Handle assignment
  const handleAssign = async () => {
    if (!selectedId) return;
    
    setIsAssigning(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const assignedTo = selectedType === 'tuner' 
      ? tuners.find(t => t.id === selectedId)
      : selectedType === 'group'
        ? groups.find(g => g.id === selectedId)
        : providers.find(p => p.id === selectedId);

    onAssign?.({
      type: selectedType,
      id: selectedId,
      name: assignedTo?.name,
      orderId,
    });
    
    setIsAssigning(false);
  };

  const getWorkloadColor = useCallback(
    (current: number, max: number) => {
      const percentage = (current / max) * 100;
      if (percentage >= 90) return token.colorError;
      if (percentage >= 70) return token.colorWarning;
      return token.colorSuccess;
    },
    [token],
  );

  const selectedBg = useMemo(
    () => hexToRgba(token.colorPrimary, 0.1),
    [token],
  );
  const memberAvatarColors = useMemo(
    () => [chartColors[0], chartColors[3], chartColors[2]],
    [chartColors],
  );

  // Tuner Card Component
  const TunerCard = ({ tuner }) => {
    const isSelected = selectedId === tuner.id && selectedType === 'tuner';
    const workloadPercent = Math.round((tuner.workload / tuner.maxWorkload) * 100);
    
    return (
      <Card
        hoverable
        onClick={() => handleSelect(tuner.id, 'tuner')}
        style={{
          borderColor: isSelected ? token.colorPrimary : undefined,
          background: isSelected ? selectedBg : undefined,
        }}
        styles={{ body: { padding: 16 } }}
      >
        <Flex gap={12} align="start">
          <Badge
            dot
            status={tuner.available ? 'success' : 'default'}
            offset={[-4, 36]}
          >
            <Avatar
              size={48}
              style={{ backgroundColor: tuner.color, fontWeight: 600 }}
            >
              {tuner.avatar}
            </Avatar>
          </Badge>
          
          <Flex vertical style={{ flex: 1 }} gap={4}>
            <Flex justify="space-between" align="center">
              <Space>
                <Text strong>{tuner.name}</Text>
                {tuner.role === 'Senior Tuner' && (
                  <Tooltip title="Senior Tuner">
                    <CrownOutlined style={{ color: token.colorWarning }} />
                  </Tooltip>
                )}
              </Space>
              {isSelected && <CheckOutlined style={{ color: token.colorPrimary }} />}
            </Flex>
            
            <Text type="secondary" style={{ fontSize: 12 }}>{tuner.role}</Text>
            
            <Flex gap={4} wrap="wrap" style={{ marginTop: 4 }}>
              {tuner.specialities.slice(0, 3).map(spec => (
                <Tag key={spec} style={{ margin: 0, fontSize: 10 }}>{spec}</Tag>
              ))}
              {tuner.specialities.length > 3 && (
                <Tag style={{ margin: 0, fontSize: 10 }}>+{tuner.specialities.length - 3}</Tag>
              )}
            </Flex>
            
            <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
              <Tooltip title={`${tuner.workload}/${tuner.maxWorkload} demandes`}>
                <Flex align="center" gap={8} style={{ flex: 1 }}>
                  <Progress
                    percent={workloadPercent}
                    size="small"
                    strokeColor={getWorkloadColor(tuner.workload, tuner.maxWorkload)}
                    showInfo={false}
                    style={{ flex: 1, maxWidth: 80 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {tuner.workload}/{tuner.maxWorkload}
                  </Text>
                </Flex>
              </Tooltip>
              
              <Space size={12}>
                <Tooltip title="Temps moyen">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <ClockCircleOutlined /> {tuner.avgTime}min
                  </Text>
                </Tooltip>
                <Tooltip title="Note">
                  <Text style={{ fontSize: 11, color: token.colorWarning }}>
                    <StarFilled /> {tuner.rating}
                  </Text>
                </Tooltip>
              </Space>
            </Flex>
            
            {tuner.currentTask && (
              <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
                <LoadingOutlined style={{ marginRight: 4 }} />
                {tuner.currentTask}
              </Text>
            )}
          </Flex>
        </Flex>
      </Card>
    );
  };

  // Group Card Component
  const GroupCard = ({ group }) => {
    const isSelected = selectedId === group.id && selectedType === 'group';
    const workloadPercent = Math.round((group.totalWorkload / group.maxWorkload) * 100);
    
    return (
      <Card
        hoverable
        onClick={() => handleSelect(group.id, 'group')}
        style={{
          borderColor: isSelected ? token.colorPrimary : undefined,
          background: isSelected ? selectedBg : undefined,
        }}
        styles={{ body: { padding: 16 } }}
      >
        <Flex gap={12} align="start">
          <Avatar
            size={48}
            style={{ 
              backgroundColor: group.color, 
              fontSize: 24,
            }}
          >
            {group.icon}
          </Avatar>
          
          <Flex vertical style={{ flex: 1 }} gap={4}>
            <Flex justify="space-between" align="center">
              <Text strong>{group.name}</Text>
              {isSelected && <CheckOutlined style={{ color: token.colorPrimary }} />}
            </Flex>
            
            <Text type="secondary" style={{ fontSize: 12 }}>{group.description}</Text>
            
            <Flex gap={4} align="center" style={{ marginTop: 4 }}>
              <Avatar.Group size="small" max={{ count: 3 }}>
                {group.members.map((member, i) => (
                  <Tooltip key={i} title={member}>
                    <Avatar size="small" style={{ backgroundColor: memberAvatarColors[i % 3] }}>
                      {member.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                  </Tooltip>
                ))}
              </Avatar.Group>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {group.memberCount} membres
              </Text>
            </Flex>
            
            <Flex gap={4} wrap="wrap" style={{ marginTop: 4 }}>
              {group.specialities.slice(0, 4).map(spec => (
                <Tag key={spec} style={{ margin: 0, fontSize: 10 }}>{spec}</Tag>
              ))}
              {group.specialities.length > 4 && (
                <Tag style={{ margin: 0, fontSize: 10 }}>+{group.specialities.length - 4}</Tag>
              )}
            </Flex>
            
            <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
              <Tooltip title={`${group.totalWorkload}/${group.maxWorkload} demandes`}>
                <Flex align="center" gap={8}>
                  <Progress
                    percent={workloadPercent}
                    size="small"
                    strokeColor={getWorkloadColor(group.totalWorkload, group.maxWorkload)}
                    showInfo={false}
                    style={{ width: 80 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {group.activeRequests} actives
                  </Text>
                </Flex>
              </Tooltip>
              
              <Tooltip title="Temps moyen de réponse">
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <ClockCircleOutlined /> ~{group.avgResponseTime}min
                </Text>
              </Tooltip>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    );
  };

  // Provider Card Component
  const ProviderCard = ({ provider }) => {
    const isSelected = selectedId === provider.id && selectedType === 'provider';
    
    return (
      <Card
        hoverable
        onClick={() => handleSelect(provider.id, 'provider')}
        style={{
          borderColor: isSelected ? token.colorPrimary : undefined,
          background: isSelected ? selectedBg : undefined,
        }}
        styles={{ body: { padding: 16 } }}
      >
        <Flex gap={12} align="start">
          <Badge
            count={provider.verified ? <SafetyCertificateOutlined style={{ color: token.colorSuccess }} /> : 0}
            offset={[-4, 36]}
          >
            <Avatar
              size={48}
              style={{ 
                backgroundColor: provider.color, 
                fontWeight: 600,
              }}
            >
              {provider.logo}
            </Avatar>
          </Badge>
          
          <Flex vertical style={{ flex: 1 }} gap={4}>
            <Flex justify="space-between" align="center">
              <Space>
                <Text strong>{provider.name}</Text>
                {provider.type === 'Premium Partner' && (
                  <Tag color="gold" style={{ margin: 0 }}>Premium</Tag>
                )}
                {provider.fastResponse && (
                  <Tooltip title="Réponse rapide">
                    <ThunderboltOutlined style={{ color: token.colorWarning }} />
                  </Tooltip>
                )}
              </Space>
              {isSelected && <CheckOutlined style={{ color: token.colorPrimary }} />}
            </Flex>
            
            <Text type="secondary" style={{ fontSize: 12 }}>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {provider.location}
            </Text>
            
            <Flex gap={4} wrap="wrap" style={{ marginTop: 4 }}>
              {provider.specialities.slice(0, 4).map(spec => (
                <Tag key={spec} style={{ margin: 0, fontSize: 10 }}>{spec}</Tag>
              ))}
              {provider.specialities.length > 4 && (
                <Tag style={{ margin: 0, fontSize: 10 }}>+{provider.specialities.length - 4}</Tag>
              )}
            </Flex>
            
            <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
              <Space size="middle">
                <Tooltip title={`${provider.totalJobs} jobs réalisés`}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <FireOutlined /> {provider.totalJobs.toLocaleString()}
                  </Text>
                </Tooltip>
                <Tooltip title="Temps de réponse">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <ClockCircleOutlined /> {provider.responseTime}
                  </Text>
                </Tooltip>
              </Space>
              
              <Space>
                <Rate 
                  disabled 
                  defaultValue={provider.rating} 
                  style={{ fontSize: 12 }}
                  allowHalf
                />
                <Text style={{ fontSize: 11, color: token.colorWarning }}>{provider.rating}</Text>
              </Space>
            </Flex>
            
            <Flex align="center" gap={8} style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Capacité:</Text>
              <Progress
                percent={provider.activeCapacity}
                size="small"
                strokeColor={getWorkloadColor(provider.activeCapacity, 100)}
                style={{ flex: 1, maxWidth: 120 }}
                format={p => `${p}%`}
              />
            </Flex>
          </Flex>
        </Flex>
      </Card>
    );
  };

  // Tab items
  const tabItems = [
    {
      key: 'tuner',
      label: (
        <Space>
          <UserOutlined />
          <span>Tuner</span>
          <Badge count={filteredTuners.length} showZero size="small" color={token.colorPrimary} />
        </Space>
      ),
      children: (
        <Flex vertical gap={12} style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
          {filteredTuners.length > 0 ? (
            filteredTuners.map(tuner => (
              <TunerCard key={tuner.id} tuner={tuner} />
            ))
          ) : (
            <Empty description="Aucun tuner trouvé" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Flex>
      ),
    },
    {
      key: 'group',
      label: (
        <Space>
          <TeamOutlined />
          <span>Groupe</span>
          <Badge count={filteredGroups.length} showZero size="small" color={token.colorPrimary} />
        </Space>
      ),
      children: (
        <Flex vertical gap={12} style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
          {filteredGroups.length > 0 ? (
            filteredGroups.map(group => (
              <GroupCard key={group.id} group={group} />
            ))
          ) : (
            <Empty description="Aucun groupe trouvé" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Flex>
      ),
    },
    {
      key: 'provider',
      label: (
        <Space>
          <ShopOutlined />
          <span>Prestataire</span>
          <Badge count={filteredProviders.length} showZero size="small" color={token.colorPrimary} />
        </Space>
      ),
      children: (
        <Flex vertical gap={12} style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
          {filteredProviders.length > 0 ? (
            filteredProviders.map(provider => (
              <ProviderCard key={provider.id} provider={provider} />
            ))
          ) : (
            <Empty description="Aucun prestataire trouvé" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Flex>
      ),
    },
  ];

  // Get selected entity info
  const getSelectedInfo = () => {
    if (!selectedId || !selectedType) return null;
    
    if (selectedType === 'tuner') {
      const tuner = tuners.find(t => t.id === selectedId);
      return { name: tuner?.name, type: 'Tuner', avatar: tuner?.avatar, color: tuner?.color };
    } else if (selectedType === 'group') {
      const group = groups.find(g => g.id === selectedId);
      return { name: group?.name, type: 'Groupe', avatar: group?.icon, color: group?.color };
    } else {
      const provider = providers.find(p => p.id === selectedId);
      return { name: provider?.name, type: 'Prestataire', avatar: provider?.logo, color: provider?.color };
    }
  };

  const selectedInfo = getSelectedInfo();

  return (
      <Card
        title={
          <Flex justify="space-between" align="center">
            <Space>
              <TeamOutlined />
              <span>Assigner la demande</span>
            </Space>
            <Tag color="blue">{orderId}</Tag>
          </Flex>
        }
        style={{ 
          width: 520, 
          background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
        }}
        styles={{ 
          header: { borderBottom: '1px solid rgba(255,255,255,0.06)' },
          body: { padding: 16 },
        }}
      >
        {/* Order Info */}
        <Card 
          size="small" 
          style={{ marginBottom: 16, background: 'rgba(255,255,255,0.02)' }}
          styles={{ body: { padding: 12 } }}
        >
          <Flex justify="space-between" align="center">
            <Space>
              <Avatar style={{ backgroundColor: token.colorWarning }}>🚗</Avatar>
              <div>
                <Text strong>{vehicle}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{product}</Text>
              </div>
            </Space>
            <Tag color="orange">{product}</Tag>
          </Flex>
        </Card>

        {/* Filters */}
        <Flex gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
          <Input
            placeholder="Rechercher..."
            prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.3)' }} />}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: 180 }}
            allowClear
          />
          <Select
            value={availabilityFilter}
            onChange={setAvailabilityFilter}
            style={{ width: 130 }}
            options={[
              { value: 'all', label: 'Disponibilité' },
              { value: 'available', label: '🟢 Disponible' },
              { value: 'busy', label: '🔴 Occupé' },
            ]}
          />
          <Select
            value={specialityFilter}
            onChange={setSpecialityFilter}
            style={{ width: 120 }}
            placeholder="Spécialité"
            options={allSpecialities.map(s => ({
              value: s,
              label: s === 'all' ? 'Toutes' : s,
            }))}
          />
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ width: 130 }}
            options={[
              { value: 'workload', label: '📊 Charge' },
              { value: 'rating', label: '⭐ Note' },
              { value: 'speed', label: '⚡ Rapidité' },
            ]}
          />
        </Flex>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="small"
        />

        <Divider style={{ margin: '16px 0' }} />

        {/* Selection Summary & Action */}
        <Flex justify="space-between" align="center">
          {selectedInfo ? (
            <Space>
              <Avatar style={{ backgroundColor: selectedInfo.color }}>
                {selectedInfo.avatar}
              </Avatar>
              <div>
                <Text strong>{selectedInfo.name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{selectedInfo.type}</Text>
              </div>
            </Space>
          ) : (
            <Text type="secondary">Sélectionnez un destinataire</Text>
          )}
          
          <Button
            type="primary"
            icon={isAssigning ? <LoadingOutlined /> : <CheckOutlined />}
            disabled={!selectedId}
            loading={isAssigning}
            onClick={handleAssign}
          >
            {isAssigning ? 'Assignation...' : 'Assigner'}
          </Button>
        </Flex>
      </Card>
  );
};

// Demo wrapper to show the widget
const AssignmentWidgetDemo = () => {
  const { token } = theme.useToken();
  const handleAssign = (assignment) => {
    console.log('Assigned:', assignment);
    // message.success(`Assigné à ${assignment.name}`);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(145deg, ${token.colorBgLayout} 0%, ${token.colorBgContainer} 50%, ${token.colorBgLayout} 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
    }}>
      <AssignmentWidget
        orderId="REQ-2847"
        vehicle="BMW M4 Competition"
        product="Stage 2"
        onAssign={handleAssign}
      />
    </div>
  );
};

export default AssignmentWidgetDemo;